import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers, extractApiData } from '@/api/core';
import { companyManagementKeys } from '@/api/admin/useCompanyManagement';
import { siteKeys } from '@/api/site/useSite';
import type { SiteManagerResponse } from '@packages/types/company';
import type { CreateSiteManagerRequest, UpdateSiteManagerRequest } from '@packages/types/site';

// Re-export for backward compatibility
export type SiteManager = SiteManagerResponse;

// Query Keys
const siteManagerKeys = {
  all: ['siteManagers'] as const,
  byCompany: (companyId: number) => [...siteManagerKeys.all, 'company', companyId] as const,
};

// 회사의 Site Manager 목록 조회
export const useGetSiteManagers = (companyId: number, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: siteManagerKeys.byCompany(companyId),
    queryFn: async () => {
      const response = await apiHelpers.get<SiteManager[]>('company.getSiteManagers', {
        companyId,
      });
      return extractApiData(response, { defaultValue: [] as SiteManager[] });
    },
    enabled: options?.enabled !== false && !!companyId,
  });
};

// Site Manager 관련 mutation 후 함께 무효화할 쿼리들
// - 오른쪽: siteManagers 목록 / 왼쪽: 사이트 행의 site_manager_name 등은 sitesByCompany 에서 옴 → 둘 다 갱신
const invalidateSiteManagerRelatedQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  companyId: number
) => {
  queryClient.invalidateQueries({ queryKey: siteManagerKeys.byCompany(companyId) });
  queryClient.invalidateQueries({ queryKey: siteKeys.details() });
  queryClient.invalidateQueries({ queryKey: companyManagementKeys.sitesByCompany(companyId) });
  queryClient.invalidateQueries({ queryKey: siteKeys.list(companyId) });
  queryClient.invalidateQueries({ queryKey: ['mySites'] });
};

// Site Manager 추가
export const useCreateSiteManager = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSiteManagerRequest) => {
      const response = await apiHelpers.post<any>('company.createSiteManager', data, {
        companyId,
      });
      return extractApiData(response);
    },
    onSuccess: () => {
      invalidateSiteManagerRelatedQueries(queryClient, companyId);
    },
  });
};

// Site Manager 정보 수정
// 수정 대상 id는 훅 생성 시점이 아니라 호출 시점 data.id 를 쓴다.
// (백엔드도 URL 경로의 id 로 data.id 를 덮어쓰므로 전달 경로를 하나로 맞춘다)
export const useUpdateSiteManager = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateSiteManagerRequest) => {
      const response = await apiHelpers.put<any>(
        'company.updateSiteManager',
        data,
        {
          companyId,
          id: data.id,
        }
      );
      return extractApiData(response);
    },
    onSuccess: () => {
      invalidateSiteManagerRelatedQueries(queryClient, companyId);
    },
  });
};

// Site Manager 삭제
// 삭제 대상 id는 훅 생성 시점이 아니라 호출 시점 인자로 받는다.
// (state 로 전달하면 setState 반영 전에 mutate 가 실행돼 직전 id 로 요청됨)
export const useDeleteSiteManager = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (siteManagerId: number) => {
      const response = await apiHelpers.delete<any>('company.deleteSiteManager', {
        companyId,
        id: siteManagerId,
      });
      return extractApiData(response);
    },
    onSuccess: () => {
      invalidateSiteManagerRelatedQueries(queryClient, companyId);
    },
  });
};

